import type {RefinementCtx} from 'zod';

import {isNotNil} from 'ramda';

import type {CheckoutProfileWrite} from '../types';
import {
  getCheckoutProfileDocumentCells,
  getCheckoutProfileDocumentCellKey,
  getCheckoutProfileDocumentTypes,
} from './getCheckoutProfileDocumentCells';

export function validateCheckoutProfile(profile: CheckoutProfileWrite, context: RefinementCtx) {
  const addError = (path: (string | number)[], code: string) =>
    context.addIssue({code: 'custom', path, message: code});

  if (profile.typeOfSale === 'INTERNAL') {
    const externalFields = ['payments', 'deposit', 'foreignCurrency', 'saleRegimes'] as const;
    externalFields.forEach((field) => {
      if (isNotNil(profile[field])) {
        addError([field], 'FIELD_NOT_ALLOWED_FOR_INTERNAL');
      }
    });

    if (isNotNil(profile.documentTypes?.correctiveTaxDocument)) {
      addError(['documentTypes', 'correctiveTaxDocument'], 'FIELD_NOT_ALLOWED_FOR_INTERNAL');
    }
  }

  const paymentMethods = profile.payments?.methods.map((payment) => payment.method) ?? [];

  if (
    profile.payments?.defaultMethod === null ||
    (isNotNil(profile.payments?.defaultMethod) &&
      !paymentMethods.includes(profile.payments.defaultMethod))
  ) {
    addError(['payments', 'defaultMethod'], 'DEFAULT_NOT_IN_ALLOWED');
  }

  (profile.deposit?.methods ?? []).forEach((method, index) => {
    if (!paymentMethods.includes(method)) {
      addError(['deposit', 'methods', index], 'DEPOSIT_METHOD_NOT_ENABLED');
    }
  });

  if (
    profile.deposit?.defaultMethod === null ||
    (isNotNil(profile.deposit?.defaultMethod) &&
      !profile.deposit.methods?.includes(profile.deposit.defaultMethod))
  ) {
    addError(['deposit', 'defaultMethod'], 'DEFAULT_NOT_IN_ALLOWED');
  }

  if (
    profile.documentTypes?.correctiveTaxDocument &&
    !getCheckoutProfileDocumentTypes(profile).includes('TAX_DOCUMENT')
  ) {
    addError(['documentTypes', 'correctiveTaxDocument'], 'DOCUMENT_ROW_NOT_APPLICABLE');
  }

  const applicable = new Set(
    getCheckoutProfileDocumentCells(profile).map(getCheckoutProfileDocumentCellKey)
  );

  const seen = new Set<string>();
  profile.documents.forEach((row, index) => {
    const key = getCheckoutProfileDocumentCellKey(row);

    if (!applicable.has(key)) {
      addError(['documents', index], 'DOCUMENT_ROW_NOT_APPLICABLE');
    }

    if (seen.has(key)) {
      addError(['documents', index], 'DOCUMENT_ROW_DUPLICATE');
    }

    seen.add(key);

    if (
      row.allowedSeriesIds.length > 0 &&
      isNotNil(row.defaultSeriesId) &&
      !row.allowedSeriesIds.includes(row.defaultSeriesId)
    ) {
      addError(['documents', index, 'defaultSeriesId'], 'DEFAULT_NOT_IN_ALLOWED');
    }

    if (
      row.allowedTemplateIds.length > 0 &&
      isNotNil(row.defaultTemplateId) &&
      !row.allowedTemplateIds.includes(row.defaultTemplateId)
    ) {
      addError(['documents', index, 'defaultTemplateId'], 'DEFAULT_NOT_IN_ALLOWED');
    }
  });
}
